import { DecimalPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { Store } from "@ngxs/store";
import { ENVIRONMENT, Environment, UnitPipe, ViewState } from "@vattenfall/core";
import { FlowsMoveAcquisitionPrivateSwitchConsumptionViewModel } from "@vattenfall/self-service";
import { isNullable, isValidGridAreaId } from "@vattenfall/util";
import { Subscription } from "rxjs";

import { FlowsMoveAcquisitionPrivateSwitchAction } from "./flows-move-acquisition-private-switch.actions";
import { FlowsMoveAcquisitionPrivateSwitchModel } from "./flows-move-acquisition-private-switch.model";
import { VIEWSTATES } from "./flows-move-acquisition-private-switch.viewstates";

const MEDIUM_CONSUMPTION_LOW = 300000;
const MEDIUM_CONSUMPTION_ZERO = 0;
/**
 * range:
 * This list of values is used to get the approximate values of consumption for predefined areas.
 * example:
 * [10,20] will return [{livingArea:10, estimatedConsumption...}, {livingArea:20, estimatedConsumption...}]
 */
const LIVING_AREA: number[] = [
  25, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200, 215, 230, 245, 260, 280, 300,
];
/** Default building type size to be set in the input slider*/
const DEAFULT_VILLA_SIZE = 120;
const DEAFULT_APARTMENT_SIZE = 100;
@Component({
  selector: "flows-move-acquisition-private-switch",
  templateUrl: "./flows-move-acquisition-private-switch.element.html",
  styleUrls: ["./flows-move-acquisition-private-switch.element.scss"],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowsMoveAcquisitionPrivateSwitchElement
  extends ViewState(FlowsMoveAcquisitionPrivateSwitchModel)
  implements OnInit, OnDestroy
{
  /**
   * The URL for moving customers to redirect to upon a successful form submission
   */
  @Input("is-moving-redirect-url") isMovingRedirectUrl: string | null = null;
  /**
   * The URL for non-moving customers to redirect to upon a successful form submission
   */
  @Input("is-not-moving-redirect-url") isNotMovingRedirectUrl: string | null = null;
  /**
   * The flag to display premie for move flow
   */
  @Input("has-active-move-premie-campaign") hasActiveMovePremieCampaign?: string;
  /**
   * The flag to display premie for acquisitionflow
   */
  @Input("has-active-acquisition-premie-campaign") hasActiveAcquisitionPremieCampaign?: string;
  /**
   * Whether the element successfully fetched postal code data
   */
  get isCompletedPostalCode(): boolean | undefined {
    return !isNullable(this.postalCode) && !isNullable(this.postalTown);
  }
  /**
   * Whether the gridarea is a valid or not
   */
  get isValidGridArea(): boolean | undefined {
    if (!this.gridArea) return false;
    else return isValidGridAreaId(this.gridArea) ? true : false;
  }

  /**
   * Formcontrols for
   *  isMoving
   *  postalCode
   *  gridArea
   *  annualEstimatedConsumption
   *  buildingAreaSliderValue
   *  buildingType
   **/
  flowsMoveAcquisitionPrivateSwitchForm?: FormGroup;
  // common
  isMovingFormControl = new FormControl(null);
  postalCodeFormControl = new FormControl(null);
  gridAreaFormControl = new FormControl(null);
  // range
  buildingAreaSliderValueFormControl!: FormControl;
  buildingTypeFormControl = new FormControl({ value: null, disabled: true });
  annualEstimatedConsumptionFormControl!: FormControl;

  private postalCodeSubscription?: Subscription;
  private gridAreaSubscription?: Subscription;
  private buildingTypeSubscription?: Subscription;
  private annualEstimatedConsumptionSubscription?: Subscription;
  private buildingAreaSliderValueSubscription?: Subscription;
  /** This value is preset due to implementation of unit pipe in combination with dynamic value*/
  public estimatedKwhConsumptionUnit: string | undefined | null = null;
  public houseAreaSliderSelected!: number;

  constructor(
    public store: Store,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    @Inject(ENVIRONMENT)
    private environment: Environment,
    private changeDefectRef: ChangeDetectorRef
  ) {
    super();
    super.viewStateInit(
      {
        "flowsMoveAcquisitionPrivateSwitch.state": null,
        "flowsMoveAcquisitionPrivateSwitch.error": null,
        "flowsMoveAcquisitionPrivateSwitch.postalTown": null,
        "flowsMoveAcquisitionPrivateSwitch.energyArea": null,
        "flowsMoveAcquisitionPrivateSwitch.isBetweenGridAreas": null,
        "flowsMoveAcquisitionPrivateSwitch.annualEstimatedConsumption": null,
        "flowsMoveAcquisitionPrivateSwitch.isMoving": null,
        "flowsMoveAcquisitionPrivateSwitch.gridArea": (gridArea: string | null) => {
          this.gridAreaFormControl.setValue(gridArea, { emitEvent: false });
          this.gridArea = gridArea;
        },
        "flowsMoveAcquisitionPrivateSwitch.postalCode": (postalCode: string) => {
          this.postalCode = postalCode;
        },
      },
      {
        store,
        elementRef,
        renderer,
      },
      VIEWSTATES
    );
    // TODO: remove this timer that forces changedetection and replace with usage of angular lifecyclehooks. This timer with forced changedetection conflicts with the angular update process. It also consumes cpu.
    setInterval(() => {
      this.changeDefectRef.detectChanges();
    });
  }

  /**
   * Validate input necessary for the component.
   * Throws error to provide a short stack trace.
   * Updates state with error.
   */
  validateInputProperties(): void {
    const isInputPropertiesInvalid = [this.isMovingRedirectUrl, this.isNotMovingRedirectUrl].some(
      (inputProperty) => !inputProperty
    );
    if (isInputPropertiesInvalid) {
      throw "[ Vattenfall Elements ] Could not instantiate <flows-move-acquisition-private-switch>. Missing required input/s.";
    }
  }

  /**
   * @description Validation to match two input values
   * @param firstControlName
   * @param secondControlName
   * @param errorKey
   */
  eitherConsumptionRequired(
    firstControlName: string,
    secondControlName: string,
    errorKey: string = firstControlName
  ): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const consumption = this.estimatedKwhConsumption;
      const globalEstimationHasValue = !!consumption && consumption > 0;
      const globalEstimationBelowMediumLow = consumption && consumption <= MEDIUM_CONSUMPTION_LOW;

      if (globalEstimationHasValue && globalEstimationBelowMediumLow) {
        form.get(firstControlName)?.setErrors(null);
        form.get(secondControlName)?.setErrors(null);
        return null;
      } else {
        form.get(firstControlName)?.setErrors({ [errorKey]: true });
        form.get(secondControlName)?.setErrors({ [errorKey]: true });
        return {
          [errorKey]: true,
        };
      }
    };
  }

  /**
   * @description Validation to match two input values
   * @param firstControlName
   * @param secondControlName
   * @param errorKey
   */
  eitherGridOrEnergyRequired(firstControlName: string, errorKey: string = firstControlName): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const energyArea = this.energyArea;
      if (energyArea || this.isValidGridArea) {
        form.get(firstControlName)?.setErrors(null);
        return null;
      } else {
        form.get(firstControlName)?.setErrors({ [errorKey]: true });
        return {
          [errorKey]: true,
        };
      }
    };
  }

  ngOnInit() {
    try {
      // The initialization will be interrupted for invalid input properties.
      this.validateInputProperties();

      this.buildingAreaSliderValueFormControl = new FormControl(null);
      this.annualEstimatedConsumptionFormControl = new FormControl(null);

      this.flowsMoveAcquisitionPrivateSwitchForm = new FormGroup(
        {
          gridArea: this.gridAreaFormControl,
          isMoving: this.isMovingFormControl,
          postalCode: this.postalCodeFormControl,
          buildingAreaSliderValue: this.buildingAreaSliderValueFormControl,
          buildingType: this.buildingTypeFormControl,
          annualEstimatedConsumption: this.annualEstimatedConsumptionFormControl,
        },
        [
          this.eitherConsumptionRequired("annualEstimatedConsumption", "buildingAreaSliderValue"),
          this.eitherGridOrEnergyRequired("gridArea"),
        ]
      );

      /** Helperfunction to create repeated pattern for controls that updates the state. */
      const handleValidated =
        (formControl: FormControl) => (p: { onValid: Function; onNotValid: Function }) => (s: string) =>
          formControl.valid ? p.onValid(s) : p.onNotValid(s);
      /**
       * This method will update state for valid postalCode.
       * - state 'loading-postalcode'| 'completed' | 'errored'
       * - gridArea
       * - postalCode
       * - postalTown
       * - isBetweenGridAreas
       * This will result in updates of formcontrols component global properties
       * for gridArea and postalCode
       * resulting in execution of gridAreaHandler
       */
      const postalCodeHandler = handleValidated(this.postalCodeFormControl)({
        onValid: (postalCode: string) =>
          this.store.dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Fetch.PostalCode(postalCode)),
        onNotValid: () =>
          this.postalCode !== null &&
          this.store.dispatch(
            new FlowsMoveAcquisitionPrivateSwitchAction.Changed({
              postalCode: null,
            })
          ),
      });
      /**
       * This method will update state whenever formcontrol of gridarea is updated.
       * - state "loading-gridarea" | "completed" | "errorred"
       * - gridArea
       * - energyArea
       *
       * When gridarea is not valid, component global values are used to replace
       * state from its payload.
       */
      const gridAreaHandler = handleValidated(this.gridAreaFormControl)({
        onValid: (gridArea: string) =>
          this.store.dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Fetch.GridArea(gridArea)),
        onNotValid: (gridArea: string) =>
          this.store.dispatch(
            new FlowsMoveAcquisitionPrivateSwitchAction.Changed({
              gridArea,
              postalCode: this.postalCode,
              postalTown: this.postalTown,
              isBetweenGridAreas: this.isBetweenGridAreas,
            })
          ),
      });

      /**
       * with range slider:
       * This handler is used when type of building is selected.
       * It requires the array of livingArea to be set.
       * The state is updated with:
       * - state: "loading-consumption" | "completed" | "error"
       * - annualEstimatedConsumption
       */
      const buildingTypeHandler = handleValidated(this.buildingTypeFormControl)({
        onValid: (buildingType: string) => {
          this.annualEstimatedConsumptionFormControl.reset();
          this.store.dispatch(
            new FlowsMoveAcquisitionPrivateSwitchAction.Fetch.Consumption({
              postalCode: this.postalCode,
              buildingType,
              livingArea: LIVING_AREA,
            })
          );
        },
        onNotValid: () => {
          /* No validation is done of the radio input options. */
        },
      });

      // subscribe to common changes;
      this.postalCodeSubscription = this.subscribeTo(this.postalCodeFormControl, postalCodeHandler);
      this.gridAreaSubscription = this.subscribeTo(this.gridAreaFormControl, gridAreaHandler);

      // subscribe to formcontrols for range
      this.buildingTypeSubscription = this.subscribeTo(this.buildingTypeFormControl, buildingTypeHandler);

      this.annualEstimatedConsumptionSubscription = this.subscribeTo(
        this.annualEstimatedConsumptionFormControl,
        (consumptionValue: number) => (this.estimatedKwhConsumption = consumptionValue)
      );

      this.buildingAreaSliderValueSubscription = this.subscribeTo(
        this.buildingAreaSliderValueFormControl,
        (consumptionValue: number) => (this.estimatedKwhConsumption = consumptionValue)
      );
    } catch (e) {
      console.error(e);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.postalCodeSubscription) this.postalCodeSubscription?.unsubscribe();
    if (this.gridAreaSubscription) this.gridAreaSubscription?.unsubscribe();
    if (this.buildingTypeSubscription) this.buildingTypeSubscription?.unsubscribe();
    if (this.annualEstimatedConsumptionSubscription) this.annualEstimatedConsumptionSubscription?.unsubscribe();
    if (this.buildingAreaSliderValueSubscription) this.buildingAreaSliderValueSubscription?.unsubscribe();
  }

  onSubmit() {
    const payload = {
      redirectUrl: this.getRedirectUrl(),
      estimatedKwhConsumption: this.estimatedKwhConsumption,
      postalCode: this.postalCode,
      postalTown: this.postalTown,
      gridArea: this.gridAreaFormControl.value,
      energyArea: this.energyArea,
    };
    this.store.dispatch(new FlowsMoveAcquisitionPrivateSwitchAction.Submitted(payload));
  }

  getRedirectUrl(): string | null {
    if (this.isMovingFormControl.value === undefined || this.isMovingFormControl.value === null)
      throw new Error(
        'flows-move-acquisition-private-switch:getRedirectUrl: shall not return url until "move" or "acquisition" is selected.'
      );
    return this.isMovingFormControl.value ? this.isMovingRedirectUrl : this.isNotMovingRedirectUrl;
  }

  private subscribeTo(control: FormControl, handler: any): Subscription {
    return control.valueChanges.subscribe(handler);
  }

  /**
   * Find a value in a list of presets.
   * example:
   * Closest value to '23' in [10,20,30,40] is
   * @param increments Array of numbers growing from smaller to larger
   * @param fromValue Value to find a close increment
   */
  toClosestValue(increments: number[], fromValue: number): number {
    return increments.reduce((prev: number, curr: number) =>
      Math.abs(curr - fromValue) < Math.abs(prev - fromValue) ? curr : prev
    );
  }

  unitCodeConverter(value: number, unit: string) {
    // TODO: Can not use dynamic value from environment: "Error: InvalidPipeArgument: 'Missing locale data for the locale "sv".' for pipe 'DecimalPipe'"
    // TODO: compare the use of let mockEnvironment = { language: "sv", locale: "sv-SE" } as Environment in the spec file for unit.pipe;
    // const decimalPipe = new DecimalPipe(this.environment.language);
    // "en" can be used, but what could better be used?
    const decimalPipe = new DecimalPipe("en");
    const pipe: UnitPipe = new UnitPipe(this.environment, decimalPipe);
    const kwhUnit = pipe.transform({ value, unit });
    // TODO: finding way to extract the unit of KWH translated from the unit pipe.
    // Can this method be used without the 'split' to render the entire dynamic unit-value?
    return kwhUnit?.split(" ")[1];
  }

  /**
   * Extract the unit from the array of consumption data and provide as a unit to use in the template besides a dynamically created value.
   */
  getConsumptionUnitValue(): string {
    this.defaultConsumptionForBuildingType();
    if (!this.annualEstimatedConsumption?.length) return "";
    if (this.estimatedKwhConsumptionUnit) return this.estimatedKwhConsumptionUnit;
    this.estimatedKwhConsumptionUnit = this.unitCodeConverter(
      0,
      this.annualEstimatedConsumption[0].estimatedConsumption?.unit as string
    );
    return this.estimatedKwhConsumptionUnit || "";
  }

  /**
   * This method compares the current input range slider value to a list of steps to find
   * the closest proximate value. This value is then used to find the corresponding
   * energy value.
   */
  getConsumptionForBuildingArea() {
    if (!this.annualEstimatedConsumption?.length)
      throw new Error("getConsumptionForBuildingArea requires list of annualEstimatedConsumption");
    const livingArea: number[] = this.annualEstimatedConsumption.map(
      ({ livingArea }: FlowsMoveAcquisitionPrivateSwitchConsumptionViewModel) =>
        (livingArea !== null && livingArea) as number
    );
    const closestAreaVal = this.toClosestValue(livingArea, this.houseAreaSliderSelected);
    const kwhForSelectedArea = (
      this.annualEstimatedConsumption as FlowsMoveAcquisitionPrivateSwitchConsumptionViewModel[]
    ).filter((areaKwh: FlowsMoveAcquisitionPrivateSwitchConsumptionViewModel) => areaKwh.livingArea === closestAreaVal);
    if (!kwhForSelectedArea.length) throw new Error("getConsumptionForBuildingArea requires matching list livingArea");
    return kwhForSelectedArea[0].estimatedConsumption?.value;
  }

  /**
   * Whether the estimated consumption 0 kWh or less
   */
  get isConsumptionLow(): boolean {
    return !!this.estimatedKwhConsumption && this.estimatedKwhConsumption <= MEDIUM_CONSUMPTION_ZERO;
  }

  /**
   * Whether the estimated consumption over 300 000 kWh
   */
  get isConsumptionHigh(): boolean {
    return !!this.estimatedKwhConsumption && this.estimatedKwhConsumption > MEDIUM_CONSUMPTION_LOW;
  }

  /**
   * Life cycle hook fucntion to set the default values for house area and estimated annual consumption data for each of the house types
   */
  defaultConsumptionForBuildingType() {
    if (this.state === "completed" && this.annualEstimatedConsumption != null) {
      if (this.buildingTypeFormControl.value === "villa" && this.estimatedKwhConsumption == null) {
        const kwhForSelectedArea = this.annualEstimatedConsumption?.filter(
          (areaKwh: FlowsMoveAcquisitionPrivateSwitchConsumptionViewModel) => areaKwh.livingArea == DEAFULT_VILLA_SIZE
        );
        this.buildingAreaSliderValueFormControl.patchValue(DEAFULT_VILLA_SIZE);
        this.estimatedKwhConsumption = kwhForSelectedArea?.length
          ? kwhForSelectedArea[0].estimatedConsumption?.value
          : null;
      } else if (this.buildingTypeFormControl.value === "apartment" && this.estimatedKwhConsumption == null) {
        const kwhForSelectedArea = this.annualEstimatedConsumption?.filter(
          (areaKwh: FlowsMoveAcquisitionPrivateSwitchConsumptionViewModel) =>
            areaKwh.livingArea == DEAFULT_APARTMENT_SIZE
        );
        this.buildingAreaSliderValueFormControl.patchValue(DEAFULT_APARTMENT_SIZE);
        this.estimatedKwhConsumption = kwhForSelectedArea?.length
          ? kwhForSelectedArea[0].estimatedConsumption?.value
          : null;
      }
    }
  }

  /**
   * House Area selected from the vf-input-range-slider component
   *
   */
  houseAreaSelected(areaSelected: number) {
    this.houseAreaSliderSelected = areaSelected;
    this.estimatedKwhConsumption = this.getConsumptionForBuildingArea();
  }

  scrollToPosition() {
    const flowsComponent = document.querySelector("flows-move-acquisition-private-switch");
    const element =
      flowsComponent?.shadowRoot?.querySelector(".ui-bg-background-blue") ||
      flowsComponent?.shadowRoot?.querySelector("[flowsmoveacquisitionprivateswitchstep2deliveryheading]");

    if (element) {
      const rect = element.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const scrollPosition = absoluteTop - 80;

      window.scrollTo({ top: scrollPosition, behavior: "smooth" });
    }
  }

  enableDisable() {
    if (!this.isCompletedPostalCode && !this.postalCodeFormControl.valid) {
      this.buildingTypeFormControl.reset();
      this.buildingTypeFormControl.disable();
    } else {
      this.buildingTypeFormControl.reset();
      this.buildingTypeFormControl.enable();
    }
  }

  // Block external eventhandlers from thrashing the input range.
  stopEventPropagation(e: MouseEvent) {
    e.stopPropagation();
  }

  debugForm(e: Event) {
    e.preventDefault();
    console.log(this.flowsMoveAcquisitionPrivateSwitchForm);
  }
  /* Todo: in use until unittests are complete
  Add to template:
  <button (click)="debugForm($event)">debug form</button>
  */
}
